import { createContext, ReactNode, useEffect, useMemo, useState } from 'react';

import { SubscriptionsState } from 'src/@types/feature';
import useMerchant from '../hooks/useMerchant';
import { cancelSubscriptionAPI, updatePaymentMethodAPI } from 'src/resources/api/subscription/index.api';
import { useQueryMerchantFeaturesGet } from 'src/resources/queries/features.queries';
import {
  useMutationSaasSubCreate, 
  useMutationSaasSubUpdate, 
  useQueryMerchantSubscriptionGet,
  useQuerySubscriptionsList
} from 'src/resources/queries';
// import { appcuesIdentifyMerchantSubscriptions, appcuesTrackEvent } from "../utils/appcues";
import useAuth from 'src/hooks/useAuth';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { BASE_URL } from "src/config";
import { DEFAULT_CURRENCY } from 'src/utils/priceFormatter';

const SubscriptionsContext = createContext<SubscriptionsState>({} as SubscriptionsState);

type SubscriptionsProviderProps = {
  children: ReactNode;
};

//

export enum PackageCodes {
  EVENT_OFFSETTING = 'EVENT_OFFSETTING',
  BUSINESS_OFFSETTING = 'BUSINESS_OFFSETTING',
  TRAVEL_AND_TOURS_OFFSETTING = 'TRAVEL_AND_TOURS_OFFSETTING',
  OFFSET_GUEST_STAYS = 'OFFSET_GUEST_STAYS',
}

//

const SubscriptionsContextProvider = ({ children }: SubscriptionsProviderProps) => {
  const [planCurrency, setPlanCurrency] = useState('');

  const { merchant, isMerchantLoading } = useMerchant();
  const { user } = useAuth();

  const getMerchantFeaturesHook = useQueryMerchantFeaturesGet({
    variables: { merchantCode: merchant?.code! },
    enabled: !!merchant?.code,
  });

 const features = useMemo(
    () => getMerchantFeaturesHook?.data?.features || [],
    [getMerchantFeaturesHook?.data?.features]
  );

  const getAllSubscriptionHook = useQuerySubscriptionsList({
    enabled: !!merchant?.code,
  });

  const subscriptionPlans = useMemo(() => {
    return getAllSubscriptionHook?.data?.content || [];
  }, [getAllSubscriptionHook?.data?.content]);

  const availablePlanCurrencies = useMemo(() => {
    const list = subscriptionPlans?.[0]?.prices;
    return [...new Set(list?.map((item) => item?.currency?.toUpperCase()))];
  }, [subscriptionPlans]);

  const getMerchantSubscriptionHook = useQueryMerchantSubscriptionGet({
    variables: { merchantCode: merchant?.code! },
    enabled: !!merchant?.code,
  });

  const merchantSubscriptions = useMemo(() => {
    return getMerchantSubscriptionHook?.data || {
      currentSubscriptions: [],
      expiredSubscriptions: [],
      trialsAvailable: []
    };
  }, [getMerchantSubscriptionHook?.data]);

  const currentPaidPlan = useMemo(() => 
    merchantSubscriptions?.currentSubscriptions?.
      find((subscription) => !subscription.trial && subscription?.planLevel > 10),
  [merchantSubscriptions?.currentSubscriptions]);

  useEffect(() => {
    if (merchant) {
      if (currentPaidPlan?.price?.currency) {
        setPlanCurrency(currentPaidPlan?.price?.currency?.toLocaleUpperCase());
      } else if (availablePlanCurrencies.includes(merchant?.currency)) {
        setPlanCurrency(merchant.currency);
      } else {
        setPlanCurrency(DEFAULT_CURRENCY);
      }
    }
  }, [currentPaidPlan, merchant, isMerchantLoading, availablePlanCurrencies, availablePlanCurrencies?.length]);

  const currentPlan = useMemo(() => {
    return merchantSubscriptions?.currentSubscriptions?.find((el) => !el?.planLevel && !el?.trial);
  }, [merchantSubscriptions]);

  const getPlanByPlanLevel = (packageCode: string) => {
    if (subscriptionPlans) {
      return subscriptionPlans?.find(plan => plan?.planPackageCode === packageCode);
    }
  }

  const createCheckoutSession = useMutationSaasSubCreate({
    onSuccess: (data) => window.location.href = data.checkoutUrl,
  });

  const updateSaasSubscription = useMutationSaasSubUpdate({
    onSuccess: (data) => window.location.href = data.url,
  });

  const onSubscribeButtonClick = (planId: string) => {
    if(!user?.email || !merchant?.code || !planId) {
      return;
    }

    if (!merchantSubscriptions?.currentSubscriptions?.filter(sub => !sub.planLevel).length) {
      createCheckoutSession.mutate({
        merchantCode: merchant?.code,
        customerEmail: user.email,
        priceId: planId,
        successUrl: `${BASE_URL}${PATH_DASHBOARD.user.confirmation}`,
        cancelUrl: `${BASE_URL}${PATH_DASHBOARD.user.plans}`
      })
    } else {
      updateSaasSubscription.mutate({
        merchantCode: merchant?.code,
        priceId: planId,
        successUrl: `${BASE_URL}${PATH_DASHBOARD.user.confirmation}`,
        cancelUrl: `${BASE_URL}${PATH_DASHBOARD.user.plans}`
      })
    }
  }

  const onUpdateToAnnualButtonClick = (packageCode: string, currency: string, interval: string) => {
    const planPrices = getPlanByPlanLevel(packageCode)?.prices;
    const yearlyPlanId = planPrices?.find(price => 
      price?.recurringInterval === interval && price?.currency === currency?.toLowerCase());

    if(!user?.email || !merchant?.code || !yearlyPlanId) {
      return;
    }

    const data = {
      priceId: yearlyPlanId.id,
      successUrl: `${BASE_URL}${PATH_DASHBOARD.user.confirmation}`,
      cancelUrl: `${BASE_URL}${PATH_DASHBOARD.user.plans}`
    }

    updateSaasSubscription.mutate({ merchantCode: merchant?.code, ...data })
  }

  const onCancelButtonClick = () => {
    if(!user?.email || !merchant?.code) {
      return;
    }
    const data = {
      successUrl: `${BASE_URL}${PATH_DASHBOARD.user.cancelConfirmation}`,
      cancelUrl: `${BASE_URL}${PATH_DASHBOARD.user.plans}`
    }
    cancelSubscriptionAPI(merchant?.code, data)
      .then(({ data }) => {
        console.log(data);
        window.location.href = data.url;
      })
  }

  const onUpdatePaymentMethod = async (): Promise<void> => {
    if(user?.email && merchant?.code) {
      const request = {
        successUrl: `${BASE_URL}${PATH_DASHBOARD.user.confirmation}`,
        cancelUrl: `${BASE_URL}${PATH_DASHBOARD.user.plans}`
      }
      const { data } = await updatePaymentMethodAPI(merchant?.code, request);
      window.location.href = data.url;
    }
  }

  // NOTE: ⚠️ saved for old subscriptions
  const isStripeSubscription = useMemo((): boolean => {
    return merchantSubscriptions?.currentSubscriptions?.some((subscription) => 
    subscription?.externalProviderCode === 'STRIPE' && !subscription.trial);
  }, [merchantSubscriptions?.currentSubscriptions]);

  return (
    <SubscriptionsContext.Provider
      value={{
        features: features,
        isFeaturesLoading: getMerchantFeaturesHook.isPending,
        currentPlan,
        subscriptions: merchantSubscriptions,
        subscriptionsAreLoading: getMerchantSubscriptionHook.isPending,
        subscriptionPlans,
        isPlansLoading: getAllSubscriptionHook.isPending,
        getPlanByPlanLevel,
        onSubscribeButtonClick,
        onUpdateToAnnualButtonClick,
        onCancelButtonClick,
        onUpdatePaymentMethod,
        planCurrency,
        setPlanCurrency,
        isStripeSubscription,
        availablePlanCurrencies,
        isLoadingCreateSub: createCheckoutSession?.isPending,
        isLoadingUpdateSub: updateSaasSubscription?.isPending,
      }}
    >
      {children}
    </SubscriptionsContext.Provider>
  );
}

export { SubscriptionsContext, SubscriptionsContextProvider };
